import { Component, Input } from '@angular/core';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PurchasingService } from '../../services/purchasing.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

@Component({
	selector: 'app-vendor-po-upload',
	templateUrl: './vendor-po-upload.component.html',
	styleUrls: ['./vendor-po-upload.component.scss']
})
export class VendorPoUploadComponent {
	@Input() vendor: any = null;

	files: File[] = [];
	uploadedHeaders: string[] = [];
	uploadedRawData: any[] = [];
	columnMappings: { [header: string]: string } = {};
	availableFieldTypes: string[] = ['stockid', 'qty_purchase', 'price', 'description', 'ignore'];
	processing = false;
	uploadReady = false;
	creating = false;
	poError = '';
	poSuccess = '';
	user: any = {};
	rowNumber = 1;
	builtLines: { stockid: string; quantity: number; price: number; description: string }[] = [];

	constructor(
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService
	) {
		this.globalSearchService.user.subscribe((u: any) => {
			this.user = u?.user || u || {};
		});
	}

	onDropzoneChange(event: { addedFiles: File[] }) {
		if (event.addedFiles && event.addedFiles.length) {
			this.files = event.addedFiles;
			this.processFiles();
		}
	}

	onRemove(file: File) {
		this.files = this.files.filter(f => f !== file);
		if (this.files.length === 0) {
			this.uploadedRawData = [];
			this.uploadedHeaders = [];
			this.uploadReady = false;
			this.builtLines = [];
		} else {
			this.processFiles();
		}
	}

	clearAll() {
		this.files = [];
		this.uploadedRawData = [];
		this.uploadedHeaders = [];
		this.columnMappings = {};
		this.uploadReady = false;
		this.builtLines = [];
		this.poError = '';
		this.poSuccess = '';
	}

	private readFile(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve((e.target as FileReader).result as ArrayBuffer);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}

	async processFiles() {
		if (!this.files.length) { return; }
		this.processing = true;
		this.uploadedRawData = [];
		this.uploadedHeaders = [];
		try {
			for (const file of this.files) {
				const buf = await this.readFile(file);
				const ext = (file.name || '').split('.').pop()?.toLowerCase();
				let rows: Record<string, any>[] = [];
				if (ext === 'xlsx' || ext === 'xls') {
					const workbook = XLSX.read(buf, { type: 'array' });
					const sheet = workbook.Sheets[workbook.SheetNames[0]];
					rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
				} else if (ext === 'csv') {
					const text = new TextDecoder().decode(buf);
					const lines = text.split(/\r?\n/).filter(l => l.trim());
					if (lines.length < 2) { continue; }
					const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
					rows = lines.slice(1).map(line => {
						const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
						const obj: Record<string, any> = {};
						header.forEach((h, i) => { obj[h] = values[i] ?? ''; });
						return obj;
					});
				}
				if (rows.length && Object.keys(rows[0]).length) {
					const headers = Object.keys(rows[0]);
					this.uploadedHeaders = [...new Set([...this.uploadedHeaders, ...headers])];
					rows.forEach(r => this.uploadedRawData.push({ ...r }));
				}
			}
			this.uploadedHeaders.forEach(h => {
				if (this.columnMappings[h] === undefined) { this.columnMappings[h] = 'ignore'; }
			});
			this.autoMapColumns();
			this.uploadReady = this.uploadedRawData.length > 0;
		} catch (e) {
			this.globalSearchService.showNotification('Error parsing file', 'danger', 'bottom', 'right');
		}
		this.processing = false;
	}

	private normalizeHeader(h: string): string {
		return (h || '').toLowerCase().trim().replace(/[\s_]+/g, '');
	}

	private autoMapColumns(): void {
		const aliases: { [key: string]: string[] } = {
			stockid: ['stockid', 'stock_id', 'item', 'itemcode', 'product', 'sku', 'part'],
			qty_purchase: ['qty_purchase', 'qty', 'quantity', 'qty_purchased', 'qtyordered'],
			price: ['price', 'unitprice', 'unit_price', 'unitcost', 'cost'],
			description: ['description', 'desc', 'comment', 'notes']
		};
		const types = (['stockid', 'qty_purchase', 'price', 'description'] as const);
		this.uploadedHeaders.forEach(h => {
			const n = this.normalizeHeader(h);
			for (const t of types) {
				const list = aliases[t] || [t];
				if (list.some(a => this.normalizeHeader(a) === n || n === t.replace(/_/g, ''))) {
					this.columnMappings[h] = t;
					break;
				}
			}
		});
	}

	applyMappings(): { stockid: string; quantity: number; price: number; description: string }[] {
		const details: { stockid: string; quantity: number; price: number; description: string }[] = [];
		this.uploadedRawData.forEach(row => {
			const mapped: any = {};
			Object.keys(row).forEach(header => {
				const field = this.columnMappings[header];
				if (field && field !== 'ignore') { mapped[field] = row[header]; }
			});
			const qty = parseFloat(mapped['qty_purchase']);
			if ((mapped['stockid'] || '').toString().trim() && !isNaN(qty) && qty !== 0) {
				details.push({
					stockid: (mapped['stockid'] || '').toString().trim(),
					quantity: qty,
					price: parseFloat(mapped['price']) || 0,
					description: (mapped['description'] || '').toString().trim()
				});
			}
		});
		return details;
	}

	hasRequiredMappings(): boolean {
		const used = new Set<string>();
		Object.keys(this.columnMappings).forEach(header => {
			const v = this.columnMappings[header];
			if (v && v !== 'ignore') { used.add(v); }
		});
		return used.has('stockid') && used.has('qty_purchase');
	}

	buildLines() {
		this.builtLines = this.applyMappings().slice();
		this.poError = '';
	}

	addLine() {
		this.builtLines.push({ stockid: '', quantity: 0, price: 0, description: '' });
	}

	removeLine(index: number) {
		this.builtLines.splice(index, 1);
	}

	downloadPoUploadTemplate() {
		const header = 'stockid,qty_purchase,price,description';
		const sample1 = 'ITEM001,10,25.00,Widget';
		const sample2 = 'ITEM002,5,10.50,Part B';
		const csv = [header, sample1, sample2].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, 'vendor_po_upload_template.csv');
	}

	createPo() {
		if (!this.vendor?.supplierid || !this.user?.userid) {
			this.poError = 'Vendor or user not set';
			return;
		}
		const validLines = this.builtLines.filter(l => (l.stockid || '').toString().trim() && !isNaN(l.quantity) && l.quantity !== 0);
		if (!validLines.length) {
			this.poError = 'No valid lines (stockid and quantity required per line)';
			return;
		}
		const details = validLines.map(l => ({
			stockid: (l.stockid || '').toString().trim(),
			quantity: l.quantity,
			price: l.price || 0,
			description: (l.description || '').toString().trim()
		}));
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);
		const loc = this.user.defaultlocation?.loccode || this.user.loccode || '';
		const header: any = {
			comments: 'Created from upload',
			initiator: this.user.userid || this.user.user?.userid || '',
			refrenceno: '',
			warehouse: loc,
			deliverydate: tomorrow.toISOString ? tomorrow.toISOString().slice(0, 10) : tomorrow.toLocaleDateString('en-CA'),
			deladd1: '', deladd2: '', deladd3: '', deladd4: '', deladd5: '', deladd6: '',
			address1: '', address2: '', address3: '', address4: '', address5: '', address6: ''
		};
		const lineDetails = details.map(d => ({
			stockid: d.stockid,
			quantity: d.quantity,
			price: d.price,
			description: d.description || '',
			notes: '',
			line_notes: '',
			fetcharge: 0
		}));
		this.creating = true;
		this.poError = '';
		this.poSuccess = '';
		this.purchasingService.createPurchaseOrder(this.vendor, header, lineDetails, this.user).subscribe(
			(res: any) => {
				this.creating = false;
				if (res?.success && res?.orderno) {
					this.poSuccess = 'PO #' + res.orderno + ' created';
					this.globalSearchService.showNotification(this.poSuccess, 'success', 'bottom', 'right');
					this.clearAll();
				} else {
					this.poError = res?.message || 'Create failed';
				}
			},
			err => {
				this.creating = false;
				this.poError = err?.error?.message || err?.message || 'Request failed';
			}
		);
	}
}
